import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, Upload, Send, Users, AlertTriangle, CheckCircle2, Loader2, Image as ImageIcon, RefreshCw } from "lucide-react";
import { nlCloudinaryConfig, nlRecipients, nlPreview, nlSend, adminEventsList } from "../lib/api";

const HOME_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || "https://velorajdm.com";

export const NewsletterAdmin = ({ token }) => {
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState("");
    const [subject, setSubject] = useState("");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [ctaUrl, setCtaUrl] = useState("https://velorajdm.com/");
    const [posterUrl, setPosterUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [recipients, setRecipients] = useState([]);
    const [stats, setStats] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [loadingRec, setLoadingRec] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const debRef = useRef(null);

    // Carica eventi e imposta evento 1 di default
    useEffect(() => {
        adminEventsList(token).then(d => {
            const evs = (d.events || []).filter(e => e.id);
            setEvents(evs);
            const sorted = [...evs].sort((a, b) => (a.numero || 99) - (b.numero || 99));
            if (sorted.length) setEventId(sorted[0].id);
        }).catch(() => { });
    }, [token]);

    const loadRecipients = (eid) => {
        if (!eid) return;
        setLoadingRec(true);
        nlRecipients(token, { event_id: eid }).then(d => {
            setRecipients(d.recipients || []);
            setStats(d.stats || null);
            setSelected(new Set((d.recipients || []).filter(r => r.default_selected).map(r => r.email)));
        }).catch(() => toast.error("Errore nel caricamento destinatari")).finally(() => setLoadingRec(false));
    };

    useEffect(() => { if (eventId) loadRecipients(eventId); /* eslint-disable-next-line */ }, [eventId]);

    // Anteprima email (debounced)
    useEffect(() => {
        if (debRef.current) clearTimeout(debRef.current);
        debRef.current = setTimeout(() => {
            nlPreview(token, { subject, title, body_text: body, poster_url: posterUrl, cta_url: ctaUrl })
                .then(d => setPreviewHtml(d.html || "")).catch(() => { });
        }, 500);
        return () => debRef.current && clearTimeout(debRef.current);
    }, [token, subject, title, body, posterUrl, ctaUrl]);

    const handleUpload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Carica un file immagine"); return; }
        setUploading(true);
        try {
            const cfg = await nlCloudinaryConfig(token);
            const fd = new FormData();
            fd.append("file", file);
            fd.append("upload_preset", cfg.upload_preset);
            const r = await fetch(cfg.upload_url, { method: "POST", body: fd });
            const data = await r.json();
            if (!data.secure_url) throw new Error("upload fallito");
            setPosterUrl(data.secure_url);
            toast.success("Locandina caricata");
        } catch (e) {
            toast.error("Upload locandina fallito");
        } finally { setUploading(false); }
    };

    const toggle = (email) => setSelected(prev => {
        const n = new Set(prev);
        n.has(email) ? n.delete(email) : n.add(email);
        return n;
    });
    const selectConsenting = () => setSelected(new Set(recipients.filter(r => r.marketing_consent && !r.unsubscribed).map(r => r.email)));
    const clearAll = () => setSelected(new Set());

    const selectedList = useMemo(() => recipients.filter(r => selected.has(r.email)), [recipients, selected]);
    const flaggedSelected = selectedList.filter(r => r.flags.includes("no_consent")).length;

    const handleSend = async () => {
        if (!subject.trim()) { toast.error("Inserisci l'oggetto dell'email"); return; }
        if (!selectedList.length) { toast.error("Seleziona almeno un destinatario"); return; }
        let msg = `Inviare la newsletter a ${selectedList.length} destinatari?`;
        if (flaggedSelected > 0) msg += `\n\n⚠️ ${flaggedSelected} NON hanno dato consenso marketing esplicito: li includi comunque?`;
        if (!window.confirm(msg)) return;
        setSending(true);
        setResult(null);
        try {
            const d = await nlSend(token, {
                subject, title, body_text: body, poster_url: posterUrl, cta_url: ctaUrl,
                recipients: selectedList.map(r => ({ email: r.email, name: r.name })),
            });
            setResult(d);
            toast.success(`Inviate ${d.sent} · Fallite ${d.failed} · Saltate ${d.skipped}`);
            loadRecipients(eventId);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Invio fallito");
        } finally { setSending(false); }
    };

    const SourceBadge = ({ s }) => {
        const map = { evento: ["Evento", "#4B7BEA"], newsletter: ["Newsletter", "#16a34a"], both: ["Evento+News", "#E2231A"] };
        const [label, color] = map[s] || [s, "#9AA2AD"];
        return <span style={{ color, borderColor: color }} className="text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-sm">{label}</span>;
    };

    return (
        <div data-testid="newsletter-admin" className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* ─── Composizione ─── */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-[#F4F2ED]">
                    <Mail size={20} className="text-[#E2231A]" />
                    <h3 className="font-display uppercase tracking-widest text-lg">Componi Newsletter</h3>
                </div>

                {/* Locandina */}
                <div className="bg-[#1C2026] border border-[#9AA2AD]/20 p-4">
                    <label className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-2 block">Locandina</label>
                    {posterUrl ? (
                        <div className="relative">
                            <img src={posterUrl} alt="locandina" className="w-full max-h-72 object-contain bg-black/30 rounded-sm" data-testid="newsletter-poster-preview" />
                            <button data-testid="newsletter-poster-remove" onClick={() => setPosterUrl("")} className="absolute top-2 right-2 bg-black/70 text-[#F4F2ED] text-xs px-2 py-1 uppercase tracking-wider">Rimuovi</button>
                        </div>
                    ) : (
                        <label className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#9AA2AD]/40 py-10 cursor-pointer hover:border-[#E2231A]/60 transition-colors">
                            {uploading ? <Loader2 className="animate-spin text-[#E2231A]" /> : <ImageIcon className="text-[#9AA2AD]" />}
                            <span className="text-xs text-[#9AA2AD] uppercase tracking-wider">{uploading ? "Caricamento…" : "Carica immagine"}</span>
                            <input data-testid="newsletter-poster-input" type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} />
                        </label>
                    )}
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-1 block">Oggetto email *</label>
                    <input data-testid="newsletter-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Es. VELORA JDM — Nuovo evento in notturna"
                        className="w-full bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2 text-[#F4F2ED] text-sm focus:border-[#E2231A] outline-none" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-1 block">Titolo (nell'email)</label>
                    <input data-testid="newsletter-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Es. Limatola in Notturna"
                        className="w-full bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2 text-[#F4F2ED] text-sm focus:border-[#E2231A] outline-none" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-1 block">Testo</label>
                    <textarea data-testid="newsletter-body" value={body} onChange={e => setBody(e.target.value)} rows={6} placeholder="Scrivi qui il messaggio per i clienti…"
                        className="w-full bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2 text-[#F4F2ED] text-sm focus:border-[#E2231A] outline-none resize-y" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-1 block">Link bottone "Scopri l'evento"</label>
                    <input data-testid="newsletter-cta" value={ctaUrl} onChange={e => setCtaUrl(e.target.value)}
                        className="w-full bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2 text-[#F4F2ED] text-sm focus:border-[#E2231A] outline-none" />
                </div>

                <div>
                    <div className="text-xs uppercase tracking-widest text-[#9AA2AD] mb-2">Anteprima email</div>
                    <iframe data-testid="newsletter-preview" title="anteprima" srcDoc={previewHtml} className="w-full h-[520px] bg-white border border-[#9AA2AD]/20" />
                </div>
            </div>

            {/* ─── Destinatari ─── */}
            <div className="space-y-4">
                <div className="flex items-center justify-between gap-2 text-[#F4F2ED]">
                    <div className="flex items-center gap-2">
                        <Users size={20} className="text-[#E2231A]" />
                        <h3 className="font-display uppercase tracking-widest text-lg">Destinatari</h3>
                    </div>
                    <button data-testid="newsletter-reload-recipients" onClick={() => loadRecipients(eventId)} className="text-[#9AA2AD] hover:text-[#F4F2ED]"><RefreshCw size={16} /></button>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <select data-testid="newsletter-event-select" value={eventId} onChange={e => setEventId(e.target.value)}
                        className="bg-[#15181C] border border-[#9AA2AD]/30 px-3 py-2 text-[#F4F2ED] text-sm">
                        {events.map(e => <option key={e.id} value={e.id}>{(e.numero ? e.numero + " · " : "") + (e.nome || e.id)}</option>)}
                        <option value="all">Tutti gli eventi</option>
                    </select>
                    <button data-testid="newsletter-select-consenting" onClick={selectConsenting} className="text-xs uppercase tracking-wider border border-[#16a34a]/60 text-[#16a34a] px-3 py-2 hover:bg-[#16a34a]/10">Solo con consenso</button>
                    <button data-testid="newsletter-clear-all" onClick={clearAll} className="text-xs uppercase tracking-wider border border-[#9AA2AD]/40 text-[#9AA2AD] px-3 py-2 hover:bg-[#9AA2AD]/10">Deseleziona tutti</button>
                </div>

                {stats && (
                    <div className="flex flex-wrap gap-2 text-[11px]">
                        <span className="bg-[#1C2026] border border-[#9AA2AD]/20 px-2 py-1 text-[#F4F2ED]">Totale {stats.total}</span>
                        <span className="bg-[#16a34a]/10 border border-[#16a34a]/40 px-2 py-1 text-[#16a34a]">Con consenso {stats.selectable}</span>
                        {stats.no_consent > 0 && <span className="bg-[#E2231A]/10 border border-[#E2231A]/40 px-2 py-1 text-[#E2231A]">Senza consenso {stats.no_consent}</span>}
                        {stats.unsubscribed > 0 && <span className="bg-[#9AA2AD]/10 border border-[#9AA2AD]/40 px-2 py-1 text-[#9AA2AD]">Disiscritti {stats.unsubscribed}</span>}
                        {stats.already_sent > 0 && <span className="bg-[#4B7BEA]/10 border border-[#4B7BEA]/40 px-2 py-1 text-[#4B7BEA]">Già inviata {stats.already_sent}</span>}
                    </div>
                )}

                <div className="bg-[#1C2026] border border-[#9AA2AD]/20 max-h-[460px] overflow-y-auto">
                    {loadingRec ? (
                        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#E2231A]" /></div>
                    ) : recipients.length === 0 ? (
                        <p className="text-sm text-[#9AA2AD] p-6 text-center">Nessun destinatario per questa selezione.</p>
                    ) : recipients.map(r => (
                        <label key={r.email} data-testid={`newsletter-recipient-${r.email}`}
                            className={`flex items-center gap-3 px-3 py-2 border-b border-[#9AA2AD]/10 cursor-pointer hover:bg-[#15181C] ${r.unsubscribed ? "opacity-50" : ""}`}>
                            <input type="checkbox" data-testid={`newsletter-check-${r.email}`} checked={selected.has(r.email)} disabled={r.unsubscribed}
                                onChange={() => toggle(r.email)} className="accent-[#E2231A] w-4 h-4" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#F4F2ED] truncate">{r.name || <span className="text-[#9AA2AD] italic">senza nome</span>}</div>
                                <div className="text-[11px] text-[#9AA2AD] truncate">{r.email}</div>
                            </div>
                            <SourceBadge s={r.source} />
                            {r.unsubscribed ? (
                                <span className="text-[10px] uppercase tracking-wider text-[#9AA2AD]">Disiscritto</span>
                            ) : r.already_sent ? (
                                <span className="text-[10px] uppercase tracking-wider text-[#4B7BEA] flex items-center gap-1"><CheckCircle2 size={12} /> Inviata</span>
                            ) : r.marketing_consent ? (
                                <span className="text-[10px] uppercase tracking-wider text-[#16a34a] flex items-center gap-1"><CheckCircle2 size={12} /> Consenso</span>
                            ) : (
                                <span className="text-[10px] uppercase tracking-wider text-[#E2231A] flex items-center gap-1" title="Nessun consenso marketing esplicito"><AlertTriangle size={12} /> No consenso</span>
                            )}
                        </label>
                    ))}
                </div>

                <div className="bg-[#1C2026] border border-[#9AA2AD]/20 p-4 space-y-3">
                    <div className="text-sm text-[#F4F2ED]">Selezionati: <b className="text-[#E2231A]">{selectedList.length}</b>
                        {flaggedSelected > 0 && <span className="text-[#E2231A] text-xs ml-2"><AlertTriangle size={12} className="inline" /> di cui {flaggedSelected} senza consenso</span>}
                    </div>
                    <button data-testid="newsletter-send-btn" onClick={handleSend} disabled={sending || !selectedList.length}
                        className="w-full bg-[#E2231A] hover:bg-[#C11B15] disabled:opacity-50 text-[#F4F2ED] px-5 py-3 font-display uppercase tracking-widest flex items-center justify-center gap-2 transition-colors">
                        {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {sending ? "Invio in corso…" : `Invia a ${selectedList.length}`}
                    </button>
                    {result && (
                        <div data-testid="newsletter-send-result" className="text-xs text-[#9AA2AD] border-t border-[#9AA2AD]/10 pt-3">
                            Inviate <b className="text-[#16a34a]">{result.sent}</b> · Fallite <b className="text-[#E2231A]">{result.failed}</b> · Saltate <b className="text-[#4B7BEA]">{result.skipped}</b>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewsletterAdmin;
